import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { bulkCheckPageVisibility } from '../services/pageVisibilityService';
import socket from '../socket';
import { toast } from 'react-toastify';

// Create and export the context
export const PageVisibilityContext = createContext();

export const PageVisibilityProvider = ({ children }) => {
  const { user } = useAuth();
  const [pageStatus, setPageStatus] = useState({});
  const [loading, setLoading] = useState(true);

  // Define all routes that should be checked for visibility and maintenance status
  const routesToCheck = [
    '/request',
    '/approval',
    '/return',
    '/incident',
    '/reports',
    '/add-vehicle',
    '/incident-reports',
    '/support-feedback',
    '/support-admin',
    '/support-user',
    '/human-resources',
    '/vehicle-management',
    '/work-reports',
    '/create-user',
    '/profile',
    '/return-reports',
    '/reception',
    '/admin-work',
    '/user-guide',
    '/notifications',
    '/driver-profiles',
    '/driver-schedule',
    '/all-driver-schedules',
    '/leave-request',
    '/leave-requests',
    '/admin-driver',
    '/activity-logs',
    '/fuel-management',
    '/admin-notifications',
    '/announcements',
    '/admin-announcements',
    '/privacy-policy',
    '/todos',
    '/company-documents',
    '/employee-evaluation',
    '/page-visibility'
  ];

  useEffect(() => {
    const fetchPageStatus = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        // Always fetch status from the server for all users
        console.log('Fetching page status for routes:', routesToCheck);
        const statusData = await bulkCheckPageVisibility(routesToCheck);
        console.log('Received page status data:', statusData);
        
        // Log any pages in maintenance mode
        Object.entries(statusData).forEach(([path, status]) => {
          if (status.maintenanceMode) {
            console.log(`Page ${path} is in maintenance mode with message: ${status.maintenanceMessage}`);
          }
        });
        
        setPageStatus(statusData);
      } catch (error) {
        console.error('Error fetching page status:', error);
        // Default all to visible and not in maintenance if there's an error
        const defaultStatus = {};
        routesToCheck.forEach(route => {
          defaultStatus[route] = {
            isVisible: true,
            maintenanceMode: false,
            maintenanceMessage: null
          };
        });
        setPageStatus(defaultStatus);
      } finally {
        setLoading(false);
      }
    };

    fetchPageStatus();
    
    // Set up WebSocket listener for page visibility changes
    const handlePageVisibilityChange = (data) => {
      console.log('Received page_visibility_change event:', data);
      
      // Check if the changed page is the current page
      const currentPath = window.location.pathname;
      if (data.path === currentPath) {
        console.log(`Current page ${currentPath} maintenance status changed to: ${data.maintenanceMode}`);
        
        // Update the page status in our state
        setPageStatus(prevStatus => ({
          ...prevStatus,
          [data.path]: {
            isVisible: data.isVisible,
            maintenanceMode: data.maintenanceMode,
            maintenanceMessage: data.maintenanceMessage
          }
        }));
        
        // If the page is now in maintenance mode, show a notification and reload
        if (data.maintenanceMode) {
          toast.warning('Trang này đang chuyển sang chế độ bảo trì. Trang sẽ tải lại sau 3 giây.');
          setTimeout(() => {
            window.location.reload();
          }, 3000);
        } else if (data.maintenanceMode === false) {
          // If the page is no longer in maintenance mode, show a notification and reload
          toast.success('Trang này đã thoát khỏi chế độ bảo trì. Trang sẽ tải lại sau 3 giây.');
          setTimeout(() => {
            window.location.reload();
          }, 3000);
        }
      } else {
        // Just update the state for other pages
        setPageStatus(prevStatus => ({
          ...prevStatus,
          [data.path]: {
            isVisible: data.isVisible,
            maintenanceMode: data.maintenanceMode,
            maintenanceMessage: data.maintenanceMessage
          }
        }));
      }
    };
    
    // Register the WebSocket listener
    socket.on('page_visibility_change', handlePageVisibilityChange);
    
    // Clean up the listener when the component unmounts
    return () => {
      socket.off('page_visibility_change', handlePageVisibilityChange);
    };
  }, [user]);

  // Check if a specific page is visible
  const isPageVisible = (path) => {
    // SUPER_ADMIN can see all pages
    if (user && user.role === 'SUPER_ADMIN') {
      return true;
    }
    
    // If path is not in our status map, default to visible
    return pageStatus[path] !== undefined ? pageStatus[path].isVisible : true;
  };

  // Check if a specific page is in maintenance mode
  const isPageInMaintenance = (path) => {
    // For debugging - log the check
    console.log(`Checking maintenance mode for path: ${path}`);
    console.log(`Page status for ${path}:`, pageStatus[path]);
    
    // If path is not in our status map, default to not in maintenance
    const isInMaintenance = pageStatus[path] !== undefined ? pageStatus[path].maintenanceMode : false;
    
    // For debugging - log the result
    console.log(`Is ${path} in maintenance mode? ${isInMaintenance}`);
    
    return isInMaintenance;
  };

  // Get maintenance message for a specific page
  const getMaintenanceMessage = (path) => {
    return pageStatus[path]?.maintenanceMessage || 'Trang đang trong chế độ bảo trì. Vui lòng quay lại sau.';
  };

  return (
    <PageVisibilityContext.Provider value={{ 
      pageStatus, 
      isPageVisible, 
      isPageInMaintenance,
      getMaintenanceMessage,
      loading 
    }}>
      {children}
    </PageVisibilityContext.Provider>
  );
};

export const usePageVisibility = () => {
  const context = useContext(PageVisibilityContext);
  if (!context) {
    throw new Error('usePageVisibility must be used within a PageVisibilityProvider');
  }
  return context;
};